import { useMutation, useQuery } from '@apollo/client';
import {
  CustodianNotificationChannelTypes,
  NotificationChannelType,
} from '@generated/graphql';
import { UserContext } from '@providers/userContextProvider';
import { enumValues } from '@utils/enumValues';
import dayjs from 'dayjs';
import {
  Badge,
  Box,
  Chip,
  Divider,
  IconButton,
  Pagination,
  Popover,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from 'hephaestus/web';
import { NotificationsNone as NB } from 'hephaestus/webIcons';
import { groupBy } from 'lodash-es';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useLocalization } from '../../../hooks/useLocalization';
import { NotificationItem } from './NotificationItem';
import {
  type AppBarNotification,
  type CustodianNotificationsQueryData,
  FETCH_CUSTODIAN_NOTIFICATIONS,
  FETCH_NOTIFICATIONS,
  MARK_ALL_READ,
  MARK_READ,
  type NotificationsQueryData,
} from './notifications.query';

const PAGE_SIZE = 5;

enum NotificationTabType {
  ALL = 'ALL',
  UNREAD = 'UNREAD',
}

const getVisibleNotifications = ({
  useCustodianNotifications,
  notificationTab,
  unreadData,
  allData,
  custodianData,
}: {
  useCustodianNotifications: boolean;
  notificationTab: NotificationTabType;
  unreadData: NotificationsQueryData | undefined;
  allData: NotificationsQueryData | undefined;
  custodianData: CustodianNotificationsQueryData | undefined;
}) => {
  if (useCustodianNotifications) {
    return {
      notifications:
        custodianData?.fetchCustodianNotifications?.notifications ?? [],
      totalCount: custodianData?.fetchCustodianNotifications?.totalCount ?? 0,
    };
  }
  if (notificationTab === NotificationTabType.ALL) {
    return {
      notifications: allData?.fetchNotifications?.notifications ?? [],
      totalCount: allData?.fetchNotifications?.totalCount ?? 0,
    };
  }
  return {
    notifications: unreadData?.fetchNotifications?.notifications ?? [],
    totalCount: unreadData?.fetchNotifications?.totalCount ?? 0,
  };
};

export const Notifications = () => {
  const theme = useTheme();
  const [notificationTab, setNotificationTab] = useState<NotificationTabType>(
    NotificationTabType.ALL,
  );
  const [page, setPage] = useState(1);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { t } = useTranslation('shared');
  const { localizedDate } = useLocalization();
  const { custodianConnection, activeEntity } = useContext(UserContext);

  const { userId: paramsUserId } = useParams();

  const userId = paramsUserId ?? activeEntity?.id;

  const useCustodianNotifications = !!(
    userId && custodianConnection?.enableFetchCustodianNotifications
  );

  const pagination = {
    perPage: PAGE_SIZE,
    offSet: (page - 1) * PAGE_SIZE,
    sortField: 'createdAt',
    sortDesc: true,
  };

  const { data, refetch, loading } = useQuery(FETCH_NOTIFICATIONS, {
    variables: {
      input: {
        filter: {
          notificationChannelTypes: [NotificationChannelType.InApp],
          read: false,
        },
        pagination,
      },
    },
    skip: useCustodianNotifications,
  });

  const {
    data: notificationsData,
    refetch: notificationsRefetch,
    loading: notificationLoading,
  } = useQuery(FETCH_NOTIFICATIONS, {
    variables: {
      input: {
        filter: {
          notificationChannelTypes: [NotificationChannelType.InApp],
        },
        pagination,
      },
    },
    skip: useCustodianNotifications,
  });

  const {
    data: custodianNotificationsData,
    loading: custodianNotificationLoading,
  } = useQuery(FETCH_CUSTODIAN_NOTIFICATIONS, {
    fetchPolicy: 'cache-first',
    variables: {
      input: {
        filter: {
          notificationChannelTypes: [CustodianNotificationChannelTypes.InApp],
          userId,
        },
        pagination,
      },
    },
    skip: !useCustodianNotifications,
  });

  const [markRead] = useMutation(MARK_READ);
  const [markAllRead] = useMutation(MARK_ALL_READ);

  const unreadTotal = useMemo(() => {
    if (useCustodianNotifications) {
      return (
        custodianNotificationsData?.fetchCustodianNotifications?.totalCount ?? 0
      );
    }
    return data?.fetchNotifications?.totalCount ?? 0;
  }, [useCustodianNotifications, custodianNotificationsData, data]);

  const totalCount = useMemo(
    () =>
      getVisibleNotifications({
        useCustodianNotifications,
        notificationTab,
        unreadData: data,
        allData: notificationsData,
        custodianData: custodianNotificationsData,
      }).totalCount,
    [
      useCustodianNotifications,
      notificationTab,
      data,
      notificationsData,
      custodianNotificationsData,
    ],
  );

  const groupedNotifications = useMemo(() => {
    const { notifications } = getVisibleNotifications({
      useCustodianNotifications,
      notificationTab,
      unreadData: data,
      allData: notificationsData,
      custodianData: custodianNotificationsData,
    });
    return groupBy(notifications, (notification: AppBarNotification) => {
      const createdAt = localizedDate(notification.createdAt);
      const now = localizedDate(dayjs());
      return createdAt === now ? 'today' : createdAt;
    });
  }, [
    useCustodianNotifications,
    notificationTab,
    data,
    notificationsData,
    custodianNotificationsData,
    localizedDate,
  ]);

  const isListLoading = useMemo(() => {
    if (useCustodianNotifications) {
      return custodianNotificationLoading;
    }
    if (notificationTab === NotificationTabType.ALL) {
      return notificationLoading;
    }
    return loading;
  }, [
    useCustodianNotifications,
    custodianNotificationLoading,
    notificationTab,
    notificationLoading,
    loading,
  ]);

  useEffect(() => {
    const lowerBound = (page - 1) * PAGE_SIZE + 1;
    if (totalCount < lowerBound && !isListLoading) {
      setPage(1);
    }
  }, [totalCount, page, isListLoading]);

  const handleClose = () => {
    if (!useCustodianNotifications) {
      void notificationsRefetch();
      void refetch();
    }
    setAnchorEl(null);
  };

  const showPagination = !useCustodianNotifications && totalCount > PAGE_SIZE;
  const unreadCountLabel = unreadTotal > 99 ? '99+' : String(unreadTotal);

  return (
    <>
      <IconButton
        aria-label={t('notificationsMenu.title')}
        size='small'
        onClick={(event) => {
          if (!useCustodianNotifications) {
            void notificationsRefetch();
            void refetch();
          }
          setAnchorEl(event.currentTarget);
        }}
      >
        <Badge invisible={unreadTotal === 0} variant='dot' color='error'>
          <NB fontSize='inherit' />
        </Badge>
      </IconButton>
      <Popover
        id='notification-menu'
        anchorEl={anchorEl}
        open={open}
        disableScrollLock
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ width: '370px' }}>
          <Box
            sx={{
              position: 'sticky',
              zIndex: 2,
              top: 0,
            }}
          >
            <Stack
              useFlexGap
              spacing={2}
              sx={{ p: 2, backgroundColor: theme.color.surface }}
            >
              <Typography variant='headingSmall'>
                {t('notificationsMenu.title')}
              </Typography>
              <Stack
                direction='row'
                useFlexGap
                spacing={2}
                sx={{
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Tabs
                  value={notificationTab}
                  onChange={(_event, next: NotificationTabType) =>
                    setNotificationTab(next)
                  }
                  sx={{ minWidth: 0, flex: 1 }}
                >
                  {enumValues(NotificationTabType).map((value) => (
                    <Tab
                      key={value}
                      value={value}
                      label={`${t(`notificationsMenu.${value}`)} ${value === NotificationTabType.UNREAD && unreadTotal ? `(${unreadCountLabel})` : ''}`}
                      id={`tab-${value}`}
                      aria-controls={`tabpanel-${value}`}
                      data-testid={`notification-tab-${value.toLowerCase()}`}
                    />
                  ))}
                </Tabs>
                <Chip
                  size='small'
                  variant='outlined'
                  onClick={() => {
                    void markAllRead();
                    handleClose();
                  }}
                  sx={{ flexShrink: 0 }}
                  label={t('markAllAsRead')}
                />
              </Stack>
            </Stack>
            <Divider />
          </Box>
          {isListLoading ? (
            <Skeleton variant='rounded' height='200px' sx={{ m: 2 }} />
          ) : (
            <Stack
              useFlexGap
              sx={{
                p: 2,
                overflow: 'auto',
                pb: showPagination ? 0 : undefined,
              }}
            >
              {Object.keys(groupedNotifications).map((key) => (
                <Box sx={{ mb: 1 }} key={key}>
                  <Typography
                    variant='labelLarge'
                    component='div'
                    color='textSecondary'
                  >
                    {key === 'today' ? t('notificationsMenu.today') : key}
                  </Typography>
                  <Box>
                    {groupedNotifications[key].map((notification) => (
                      <NotificationItem
                        key={notification.id}
                        notification={notification}
                        isToday={key === 'today'}
                        useCustodianNotifications={useCustodianNotifications}
                        onClose={handleClose}
                        onMarkRead={(notificationId, onCompleted) => {
                          void markRead({
                            variables: { input: { notificationId } },
                            onCompleted,
                          });
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              ))}
              {totalCount === 0 && (
                <Stack
                  useFlexGap
                  sx={{
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '200px',
                  }}
                >
                  <Typography
                    variant='bodyMedium'
                    sx={{
                      color: theme.color.onSurfaceVariant,
                      fontWeight: 500,
                    }}
                  >
                    {t('noNotifications')}
                  </Typography>
                </Stack>
              )}
            </Stack>
          )}
          {showPagination && (
            <Stack
              useFlexGap
              direction='row'
              sx={{
                justifyContent: 'flex-end',
                position: 'sticky',
                zIndex: 2,
                bottom: 0,
                backgroundColor: theme.color.surface,
                p: 2,
              }}
            >
              <Pagination
                count={Math.ceil(totalCount / PAGE_SIZE)}
                page={page}
                onChange={(_e, p) => setPage(p)}
              />
            </Stack>
          )}
        </Box>
      </Popover>
    </>
  );
};
